import { useListMemberSeparableAccumulationsQuery } from "@/App/api/member";
import { useMemo } from "react";
import { useIsDiscountUser } from "@/App/hooks";
import { CommercialBenefits } from "@/App/pages/Dashboard/Benefits/CommercialBenefits";
import { Decimal } from "decimal.js";
import { MutableAccumulationData, DisplayNetworkStatuses, MoneyAccumulationValues, Accumulators } from "@/App/types/accumulation";

export interface AccumControllerProps {
  readonly displayNetworkStatus: DisplayNetworkStatuses;
}

function planHasAccumulator(accumulator?: MoneyAccumulationValues): boolean {
  const max = new Decimal(accumulator?.max || 0);
  return !max.isZero();
}

const defaultAccumData = (): MutableAccumulationData => {
  return {
    inNetwork: {
      individual: {
        deductible: [],
        outOfPocket: [],
        planPay: [],
        planPayFertility: [],
      },
      family: {
        deductible: [],
        outOfPocket: [],
        planPay: [],
        planPayFertility: [],
      },
    },
    outOfNetwork: {
      individual: {
        deductible: [],
        outOfPocket: [],
        planPay: [],
        planPayFertility: [],
      },
      family: {
        deductible: [],
        outOfPocket: [],
        planPay: [],
        planPayFertility: [],
      },
    },
  };
};

const accumLabels: Record<string, Accumulators> = {
  "deductible": Accumulators.Deductible,
  "maximum_out_of_pocket": Accumulators.OutOfPocket,
  "plan_maximum": Accumulators.PlanPay,
  "plan_pay_fertility": Accumulators.PlanPayFertility,
};

export const AccumController = ({ displayNetworkStatus }: AccumControllerProps) => {
  const { isDiscountUser } = useIsDiscountUser();
  const { data: accumulations, isLoading: isLoadingAccumulations } = useListMemberSeparableAccumulationsQuery(true);

  const data: { accumData: MutableAccumulationData, hasPharmacy: boolean, hasMedical: boolean } = useMemo(() => {
    const accumData = defaultAccumData();
    let hasMedical = false;
    let hasPharmacy = false;

    if (!isLoadingAccumulations) {
      for (const accum of accumulations.results) {
        if (!hasMedical && accum.applies_to.includes("medical")) {
          hasMedical = true;
        }
        if (!hasPharmacy && accum.applies_to.includes("pharmacy")) {
          hasPharmacy = true;
        }

        const individualAccum = {
          max: new Decimal(accum.individual_maximum || 0),
          spent: new Decimal(accum.individual_accumulated || 0),
          remaining: new Decimal(accum.individual_maximum || 0).minus(
            new Decimal(accum.individual_accumulated || 0)),
          appliesTo: accum.applies_to,
          isEmbedded: !!accum.is_embedded,
        };
        const familyAccum = {
          max: new Decimal(accum.family_maximum || 0),
          spent: new Decimal(accum.family_accumulated || 0),
          remaining: new Decimal(accum.family_maximum || 0).minus(
            new Decimal(accum.family_accumulated || 0),
          ),
          appliesTo: accum.applies_to,
        };

        // Don't allow accum spent to go past the max, or let remaining go negative
        if (individualAccum.spent.greaterThanOrEqualTo(individualAccum.max)) {
          individualAccum.remaining = new Decimal(0);
          individualAccum.spent = individualAccum.max;
        }
        if (familyAccum.spent.greaterThanOrEqualTo(familyAccum.max)) {
          familyAccum.remaining = new Decimal(0);
          familyAccum.spent = familyAccum.max;
        }

        const networkScope = accum.network == "in_network_tier_1" ? DisplayNetworkStatuses.In : DisplayNetworkStatuses.Out;
        const accumLabel = accumLabels[accum.type];

        if (accumLabel) {
          if (planHasAccumulator(individualAccum)) {
            accumData[networkScope].individual[accumLabel].push(individualAccum);
          }
          if (planHasAccumulator(familyAccum)) {
            accumData[networkScope].family[accumLabel].push(familyAccum);
          }
        }
      }
    }
    return { accumData, hasMedical, hasPharmacy };
  }, [accumulations]);

  return (
    <>
      {!isDiscountUser && (
        <CommercialBenefits
          accumulations={data.accumData}
          hasMedical={data.hasMedical}
          hasPharmacy={data.hasPharmacy}
          displayNetworkStatus={displayNetworkStatus}
          isLoading={isLoadingAccumulations}
        />
      )}
    </>
  );
};
